import { useState, type FormEvent } from "react";

import { Wordmark } from "../../components/Wordmark";
import { t } from "../../i18n";
import { supabase } from "../../lib/supabase";

/** Supabase rejects anything shorter; checked here so the error is immediate. */
const MIN_LENGTH = 6;

interface Props {
  /** An invite asks someone to choose a first password; a reset replaces one. */
  reason: "invite" | "recovery";
  onDone: () => void;
}

/**
 * Where invite and password-reset links land.
 *
 * Until this existed, an invited user could sign in exactly once — the link
 * created a session but no password, and there was no way to set one — so they
 * were locked out as soon as it expired.
 */
export function SetPassword({ reason, onDone }: Props) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (password.length < MIN_LENGTH) {
      setError(t("Use at least {n} characters.", { n: MIN_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t("The two passwords do not match."));
      return;
    }

    setBusy(true);
    // supabase-js reports a refusal as `error`, but a network failure can still
    // throw; either way the message shows and the button comes back.
    let updateError: { message: string } | null;
    try {
      ({ error: updateError } = await supabase.auth.updateUser({ password }));
    } catch (cause) {
      updateError = {
        message: cause instanceof Error ? cause.message : String(cause),
      };
    } finally {
      setBusy(false);
    }

    if (updateError) {
      setError(updateError.message);
      return;
    }
    onDone();
  }

  return (
    <div className="login" id="setPasswordView">
      <form
        className="card"
        // Fire and forget: submit() shows its own errors.
        onSubmit={(event) => void submit(event)}
      >
        <Wordmark />
        <h1>
          {reason === "invite"
            ? t("Choose a password")
            : t("Set a new password")}
        </h1>
        <p>
          {reason === "invite"
            ? t(
                "Welcome. Pick a password and you are in — you will use it to sign in from now on.",
              )
            : t(
                "Pick a new password. This replaces the old one everywhere you are signed in.",
              )}
        </p>
        <input
          type="password"
          placeholder={t("New password")}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <input
          type="password"
          placeholder={t("Confirm password")}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <button type="submit" disabled={busy}>
          {busy ? t("Saving…") : t("Save password")}
        </button>
        {error && <div className="err">{error}</div>}
      </form>
    </div>
  );
}
